import { Avatar, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import type { CobroClienteResponse } from '@aid/shared';
import { INVOICE_TYPE } from '@aid/shared';
import { Link } from 'react-router-dom';
import { FacturaStatusTag } from './FacturaStatusTag';
import { StatusTag } from '../../../components/StatusTag';
import { FacturasTableActions } from './FacturasTableActions';
import {
  formatearPeriodo,
  formatearPeriodoCompacto,
  formatCompactDate,
  parseDateOnly,
  DATE_FORMAT,
} from '../../../lib/date';
import { formatCurrency } from '../../../lib/format';
import { i18nInstance } from '../../../locales';
import { getFacturaInitials } from '../lib/factura-table-helpers';
import type { FacturaTableGroupBy, FacturasTranslate } from './factura-table-types';
import { serverSortProps, type ServerSortState } from '../../../lib/server-sort';
import { FACTURA_TABLE_GROUP } from './factura-table-types';

const { Text } = Typography;

export interface FacturasTableColumnsOptions {
  t: FacturasTranslate;
  showClient: boolean;
  groupBy: FacturaTableGroupBy;
  /** COBROS_ANULAR permission (no new permission); drives the annul entry. */
  canAnular: boolean;
  /** COBROS_ANULAR permission (no new permission); drives the reissue entry. */
  canReissue: boolean;
  canReadClient: boolean;
  onDetail: (id: number) => void;
  onAnular: (factura: CobroClienteResponse) => void;
  onReemitir: (factura: CobroClienteResponse) => void;
  primaryColor: string;
  sort?: ServerSortState;
}

export function createFacturasTableColumns({
  t,
  showClient,
  groupBy,
  canAnular,
  canReissue,
  canReadClient,
  onDetail,
  onAnular,
  onReemitir,
  primaryColor,
  sort = {},
}: FacturasTableColumnsOptions): ColumnsType<CobroClienteResponse> {
  const dateLocale = i18nInstance.language;
  const columns: ColumnsType<CobroClienteResponse> = [
    {
      title: t('pages.cobros.factura'),
      key: 'id',
      ...serverSortProps('id', sort),
      width: showClient ? 100 : 130,
      render: (_value: unknown, record: CobroClienteResponse) => <span>#{record.id}</span>,
    },
    ...(showClient
      ? [
          {
            title: t('pages.cobros.cliente'),
            key: 'cliente',
            ...serverSortProps('cliente', sort),
            width: 250,
            render: (_value: unknown, record: CobroClienteResponse) => {
              const clienteId = record.cliente?.id ?? record.idCliente;
              const nombre =
                record.cliente?.nombre?.trim() || t('pages.cobros.clienteId', { id: clienteId });
              const aidCode = record.cliente?.ip?.trim();
              const clientName = canReadClient ? (
                <Link
                  to={`/clientes/${clienteId}/facturas`}
                  aria-label={t('pages.cobros.verFacturasCliente', { name: nombre })}
                >
                  {nombre}
                </Link>
              ) : (
                <Text strong>{nombre}</Text>
              );
              return (
                <div
                  title={aidCode ? `${nombre} · ${aidCode}` : nombre}
                  style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}
                >
                  <Avatar
                    size={24}
                    style={{
                      background: primaryColor,
                      verticalAlign: 'middle',
                      fontSize: 12,
                    }}
                    aria-hidden
                  >
                    {getFacturaInitials(record.cliente?.nombre)}
                  </Avatar>
                  <div style={{ minWidth: 0, lineHeight: 1.3 }}>
                    <div
                      style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                    >
                      {clientName}
                    </div>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      {t('pages.cobros.clienteId', { id: clienteId })}
                      {aidCode ? ` · ${t('pages.cobros.aidCode', { code: aidCode })}` : ''}
                    </Text>
                  </div>
                </div>
              );
            },
          },
        ]
      : []),
    {
      title: t('pages.cobros.tipoFactura'),
      key: 'tipoFactura',
      ...serverSortProps('tipoFactura', sort),
      width: 130,
      render: (_value: unknown, record: CobroClienteResponse) => (
        <StatusTag tone={record.tipoFactura === INVOICE_TYPE.EXTRAORDINARIA ? 'info' : 'neutral'}>
          {record.tipoFactura === INVOICE_TYPE.EXTRAORDINARIA
            ? t('pages.cobros.tipoFacturaExtraordinaria')
            : t('pages.cobros.tipoFacturaPeriodica')}
        </StatusTag>
      ),
    },
    {
      title: t('pages.cobros.periodo'),
      key: 'periodo',
      ...serverSortProps('periodo', sort),
      width: 190,
      render: (_value: unknown, record: CobroClienteResponse) => {
        const period =
          record.tipoFactura === INVOICE_TYPE.EXTRAORDINARIA
            ? t('pages.cobros.sinPeriodo')
            : (formatearPeriodoCompacto(record.fechaInicio, record.fechaFin, dateLocale) ??
              t('pages.cobros.sinPeriodo'));
        const fullPeriod = formatearPeriodo(record.fechaInicio, record.fechaFin) ?? period;
        return <span title={fullPeriod}>{period}</span>;
      },
    },
    {
      title: t('pages.cobros.emision'),
      key: 'fechaCobro',
      ...serverSortProps('fechaCobro', sort),
      width: 130,
      render: (_value: unknown, record: CobroClienteResponse) => {
        const compactDate = formatCompactDate(record.fechaCobro, dateLocale);
        if (!compactDate) return '-';
        // Date-only contract: parse component-wise (legacy ISO-midnight safe).
        const parsed = parseDateOnly(record.fechaCobro);
        const fullDate = parsed?.isValid() ? parsed.format(DATE_FORMAT) : compactDate;
        return (
          <time
            dateTime={parsed?.isValid() ? parsed.format('YYYY-MM-DD') : undefined}
            title={fullDate}
          >
            {compactDate}
          </time>
        );
      },
    },
    {
      title: t('pages.cobros.status'),
      key: 'estado',
      ...serverSortProps('estado', sort),
      width: 130,
      render: (_value: unknown, record: CobroClienteResponse) => (
        <FacturaStatusTag factura={record} />
      ),
    },
    {
      title: t('pages.cobros.valor'),
      key: 'valor',
      ...serverSortProps('valor', sort),
      align: 'right',
      width: 150,
      render: (_value: unknown, record: CobroClienteResponse) => (
        <span
          style={{
            display: 'block',
            textAlign: 'right',
            fontVariantNumeric: 'tabular-nums',
            whiteSpace: 'nowrap',
          }}
        >
          {formatCurrency(record.valor)}
        </span>
      ),
    },
    {
      title: t('common.actions'),
      key: 'actions',
      width: 150,
      render: (_value: unknown, record: CobroClienteResponse) => (
        <FacturasTableActions
          factura={record}
          canAnular={canAnular}
          canReissue={canReissue}
          onDetail={onDetail}
          onAnular={onAnular}
          onReemitir={onReemitir}
        />
      ),
    },
  ];

  return columns.filter((column) => {
    if (groupBy === FACTURA_TABLE_GROUP.CLIENTE && column.key === 'cliente') return false;
    if (groupBy === FACTURA_TABLE_GROUP.ESTADO && column.key === 'estado') return false;
    return true;
  });
}
